import React, { useEffect, useState } from "react";
import {
  Modal,
  Avatar,
  Text,
  Button,
  Grid,
  Box,
  createStyles,
  Loader,
  Divider,
  Center,
  ActionIcon,
  rem,
  RingProgress,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import fetchResourceIcon from "../assets/img/fetchingResourceLogo.svg";
import dbStoringIcon from "../assets/img/storingDBLogo.svg";
import _ from "lodash";
import epicFrame from "../assets/img/epicFrame.svg";
import connectingSg from "../assets/img/connecting.svg";
import hl7Svg from "../assets/img/hl7.png";

const useStyles = createStyles(() => ({
  flex: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  topHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#F1F9FF",
    padding: ".5rem 1rem .5rem .5rem",
    borderRadius: "8px",
    marginBottom: "24px",
  },
  detailGrid: {
    marginTop: "24px",
    marginBottom: "32px",
    border: "1px solid",
  },
  avatar: {
    border: "none",
    background: "none",
  },
  name: {
    color: "#262930",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "30px",
    letterSpacing: "0px",
  },
  ehrType: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "21px",
    letterSpacing: "0px",
    color: "#818999",
  },
  ehrTypeName: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
    letterSpacing: "0px",
    color: "#262930",
    textTransform: "capitalize",
  },
  label: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0px",
    color: "#818999",
    marginBottom: "8px",
    textTransform: "capitalize",
  },
  button: {
    textAlign: "right",
    marginTop: "32px",
    marginBottom: "10px",
  },
  value: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0px",
    color: "#262930",
    textTransform: "capitalize",
  },
  logo: {
    width: "48px",
    marginRight: "5px",
    height: "40px",
    marginTop: "3px",
  },
  stepContainer: {
    height: "140px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #C9CDD3",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  step: {
    width: "90px",
    height: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  disableText: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0px",
    textAlign: "center",
    color: "#99A0AD",
    marginTop: "10px",
  },
  enableText: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0px",
    textAlign: "center",
    color: "#465668",
    marginTop: "10px",
  },
  divider: {
    width: "20%",
  },
  icon: {
    position: "absolute",
    top: 10,
    left: 10,
  },
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  modalData?: any;
  steps: any;
  setIsPatientModalOpen: (val: boolean) => void;
  showStepsImmediately?: boolean;
  autoConfirm?: boolean;
  hl7?: boolean;
  logo?: any;
  workflowProgress?:boolean
}

const GenericInfoModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  modalData,
  steps,
  workflowProgress,
  setIsPatientModalOpen,
  showStepsImmediately,
  autoConfirm,
  hl7,
  logo
}) => {
  const [showStep, setSetshowStep] = useState(showStepsImmediately);
  const [loadingStep, setLoadingStep] = useState(0);
  const { classes } = useStyles();

  useEffect(() => {
    if (autoConfirm && isOpen) {
      onConfirm();
      // setSetshowStep(true)
    }
  }, [autoConfirm, isOpen, onConfirm]);

  useEffect(() => {
    let timer: any;
    if (showStep) {
      const timer = setTimeout(() => {
        setLoadingStep((prevStep) => (prevStep += 1));
      }, 2500);
    }

    if (loadingStep === steps.length - 1) {
      _.debounce(() => {
        setIsPatientModalOpen(false);
        onClose();
      }, 3000)();
    }
    return () => clearTimeout(timer);
  }, [loadingStep, steps.length, showStep]);

  const handleButtonClick = async () => {
    setSetshowStep(true);
    onConfirm();
  };

  interface Props {
    disabled?: boolean;
  }

const StepComponent: React.FC<Props> = ({ disabled }) => {
    const Progress = () => {
      return (
        <RingProgress
          sections={[{ value: 100, color: "teal" }]}
          size={55}
          thickness={5}
          label={
            <Center>
              <ActionIcon color="teal" variant="light" radius="xl" size="md">
                <IconCheck style={{ width: rem(22), height: rem(22) }} />
              </ActionIcon>
            </Center>
          }
        />
      );
    };

    return (
      <Box className={classes.stepContainer}>
        {steps.map((step: any, index: number) => (
          <React.Fragment key={index}>
            <Box className={classes.step}>
              {index < loadingStep ? (
                <Progress />
              ) : index === loadingStep ? (
                <Box style={{ position: "relative" }}>
                  <Loader
                    size={"lg"}
                    color="green"
                    style={{ marginRight: "8px" }}
                  />
                  <ActionIcon
                    color="gray"
                    variant="light"
                    radius="xl"
                    size="sm"
                    className={classes.icon}
                  >
                    <img
                      src={
                        index === steps.length - 1
                          ? dbStoringIcon
                          : fetchResourceIcon
                      }
                      style={{ width: rem(22), height: rem(22) }}
                    />
                  </ActionIcon>
                </Box>
              ) : (
                <img
                  src={
                    index === steps.length - 1
                      ? dbStoringIcon
                      : fetchResourceIcon
                  }
                />
              )}
              <Text mih={"22px"} mb={"40px"} className={classes.disableText}>
                {index !== steps.length - 1 && index === loadingStep
                  ? step.loadText
                  : index < loadingStep
                    ? "Done"
                    : step.label}
              </Text>
            </Box>
            {index !== steps.length - 1 && (
              <Divider mb={"1000px"} my="xs" className={classes.divider} />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => setIsPatientModalOpen(false)}
      withCloseButton={false}
      centered
      size={"xl"}
    >
      {showStep && <Modal.CloseButton />}
      {!showStepsImmediately ? (
        <Box className={classes.topHeader}>
          <Box className={classes.flex}>
            <Avatar size={50} className={classes.avatar} />
            <Box style={{ marginLeft: "10px" }}>
              <Text className={classes.name}>{modalData.title}</Text>
            </Box>
          </Box>
          <Box>
            <Text className={classes.ehrType}></Text>
            <Box className={classes.flex}>
              {/* <img src={ehrImage} alt={ehrName} className={classes.logo} /> */}
              <Text className={classes.ehrTypeName}>{modalData.sideTitle}</Text>
            </Box>
          </Box>
        </Box>
      ) : (
        <></>
      )}

      {showStepsImmediately ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            flexDirection: "column",
          }}
        >
          {hl7 ? (
            <img
              style={{
                display: "flex",
                justifyContent: "center",
                width: "170px",
                height: "80px",
              }}
              src={hl7Svg}
              alt={"epic"}
              className={classes.logo}
            />
          ) : (
            <img
              style={{
                display: "flex",
                justifyContent: "center",
                width: workflowProgress?"60%":"170px",
                height: workflowProgress?"200px":"80px",
              }}
              src={logo??epicFrame}
              alt={"epic"}
              className={classes.logo}
            />
          )}
        </Box>
      ) : (
        <></>
      )}
      {showStepsImmediately ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <Text sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "20px" }}>
            {modalData?.title}
          </Text>
        </Box>
      ) : (
        <></>
      )}

      {!showStepsImmediately && (
        <>
          <Grid gutter="xl">
            {modalData?.details.map((details: any, index: number) => (
              <Grid.Col span={index === 4 ? 12 : 3} key={index}>
                <Text className={classes.label}>{details?.label}</Text>
                <Text className={classes.value}>{details?.text}</Text>
              </Grid.Col>
            ))}
          </Grid>

          <Box className={classes.button}>
            <Button
              color="blue"
              onClick={handleButtonClick}
              disabled={showStep}
            >
              {modalData.confirmText ?? "Confirm"}
            </Button>
          </Box>
        </>
      )}

      {/* <Box className={classes.button}>
        <Button color="blue" onClick={handleButtonClick} disabled={showStep}>
          {modalData.confirmText ?? "Confirm"}
        </Button>
      </Box> */}
      {showStep && <StepComponent />}
    </Modal>
  );
};

export default GenericInfoModal;
